import { useState, useEffect } from 'react';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { get } from 'aws-amplify/api';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { CompanyProfile, UserProfile } from '../types';
import { AdminPanel } from './admin/AdminPanel';
import { AdminProfileSetup } from './admin/AdminProfileSetup';
import { FileManager } from './FileManager';
import { UserProfileManager } from './UserProfileManager';
import { CompanyManager } from './admin/CompanyManager';
import { UserManager } from './admin/UserManager';
import { UserListManager } from './admin/UserListManager';

export function LoggedInApp() {
  const { user } = useAuthenticator((context) => [context.user]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminView, setAdminView] = useState('fileManager'); // State for admin content
  const [userView, setUserView] = useState('files'); // State for regular user content
  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const checkAdminAndFetchProfile = async () => {
      setIsLoading(true);
      try {
        const session = await fetchAuthSession();
        const groups = session.tokens?.accessToken.payload['cognito:groups'];
        const adminStatus = Array.isArray(groups) && groups.includes('Admins');
        setIsAdmin(adminStatus);

        const { body } = await get({
          apiName: 'aslanwebsftpapi',
          path: '/profiles',
        }).response;
        const profile = (await body.json()) as unknown as UserProfile;

        if (profile && profile.id) {
          setUserProfile(profile);
        } else {
          setUserProfile(null);
        }

        if (adminStatus) {
          const { body: companiesBody } = await get({
            apiName: 'aslanwebsftpapi',
            path: '/companies',
          }).response;
          const companyList =
            (await companiesBody.json()) as unknown as CompanyProfile[];
          setCompanies(companyList);
        }
      } catch (error) {
        console.error('Error during initial data fetch:', error);
        setUserProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      checkAdminAndFetchProfile();
    }
  }, [user, refreshKey]);

  const renderAdminContent = () => {
    switch (adminView) {
      case 'fileManager':
        return userProfile ? <FileManager userProfile={userProfile} /> : null;
      case 'manageUsers':
        return <UserListManager />;
      case 'createNewUser':
        return (
          <UserManager
            companies={companies}
            onUserCreated={() => setAdminView('manageUsers')}
          />
        );
      case 'manageCompanies':
        return (
          <CompanyManager
            onCompaniesUpdate={() => setRefreshKey((k) => k + 1)}
          />
        );
      default:
        return userProfile ? <FileManager userProfile={userProfile} /> : null;
    }
  };

  const renderUserContent = () => {
    if (!userProfile) return null;
    switch (userView) {
      case 'files':
        return <FileManager userProfile={userProfile} />;
      case 'profile':
        return <UserProfileManager userProfile={userProfile} />;
      default:
        return <FileManager userProfile={userProfile} />;
    }
  };

  // This function renders the main content area inside the app container
  const renderPageContent = () => {
    if (isLoading) {
      return <div>Loading your profile...</div>;
    }

    if (!userProfile) {
      if (isAdmin) {
        return (
          <AdminProfileSetup
            onProfileCreated={() => setRefreshKey((k) => k + 1)}
          />
        );
      }
      return (
        <div>
          <h1>Welcome!</h1>
          <p>Your user profile is being set up by an administrator.</p>
        </div>
      );
    }

    if (!userProfile.company) {
      return (
        <p>Error: Your profile is missing required company information.</p>
      );
    }

    return (
      <>
        {/* Secondary Header with Company Info */}
        <div className='app-header'>
          <div className='company-info'>
            <img
              src={
                userProfile.company.logoUrl ||
                `https://placehold.co/120x40/eee/ccc?text=${userProfile.company.name}`
              }
              alt={`${userProfile.company.name} logo`}
              className='company-logo'
            />
            <h2>{userProfile.company.name}</h2>
          </div>
          <div>
            <span className='welcome-message'>
              Welcome, {userProfile.firstName}
            </span>
            {!isAdmin && (
              <nav style={{ display: 'inline-block', marginLeft: '20px' }}>
                <button
                  onClick={() => setUserView('files')}
                  disabled={userView === 'files'}
                >
                  Files
                </button>
                <button
                  onClick={() => setUserView('profile')}
                  disabled={userView === 'profile'}
                  style={{ marginLeft: '10px' }}
                >
                  My Profile
                </button>
              </nav>
            )}
          </div>
        </div>

        <div className='app-main'>
          {/* The main content is now decided by the render functions */}
          {isAdmin ? renderAdminContent() : renderUserContent()}
        </div>
      </>
    );
  };

  return (
    <div className='app-wrapper'>
      <header
        className='app-top-nav'
        style={{
          backgroundColor: '#392080',
          padding: '10px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <img
          src='https://backoffice.aslan.io/assets/fullLogoWhite-786d83d1.svg'
          alt='Aslan Logo'
          style={{ height: '40px' }}
        />
        <button
          onClick={() => signOut()}
          style={{ backgroundColor: 'white', color: '#392080' }}
        >
          Sign Out
        </button>
      </header>

      <div className='app-body'>
        {/* Render the sidebar for admins who have a profile */}
        {isAdmin && userProfile && (
          <AdminPanel activeView={adminView} onViewChange={setAdminView} />
        )}

        {/* Render the main, scrollable content area */}
        <div className='app-main-content'>{renderPageContent()}</div>
      </div>
    </div>
  );
}
